import { ProjectSuggestions, DirectoryOrganizations, Projects, BusinessChallenges, Users } from '@platform/client';
import { ProjectRoleType, OrgRepUser } from '@platform/client/schemas';
import { Button, Input, Modal, PillBadge } from '@platform/components';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useParams } from 'wouter';
import { navigate } from 'wouter/use-browser-location';
import { SkillsSelectionDropdown } from '../../components/SkillsSelectionDropdown';
import { ROUTES } from '../../routes/consts';
import { businessChallengeFullText } from '../../util/TextHelpers';
import { ProjectSuggestionPreview } from './ProjectSuggestionPreview';
import { ProjectSubmittedScreen } from './ProjectSubmittedScreen';
import GeneratingSuggestionScreen from './GeneratingSuggestion';

type FormValues = {
  title: string;
  description: string;
  deliverables: { value: string }[];
  learningOutcomes: string;
  skills: string[];
};

const GeneratedProjectSuggestionPage = () => {
  const { challengeId, challengeIdx } = useParams<{
    challengeId: string;
    challengeIdx: string;
  }>();
  const { data: user } = Users.useGetCurrentUser<OrgRepUser>();
  const orgId = user?.organization?.id ?? 0;
  const challengeIdValue = parseInt(challengeId);
  const challengeIdxValue = parseInt(challengeIdx);

  const {
    data: challengeData,
    isLoading: isLoadingBusinessChallenges,
  } = BusinessChallenges.useGetOrganizationBusinessChallenges(orgId);
  const existingChallenges = (challengeData?.challenges ?? []).filter(
    (challenge) => challenge.challenge && challenge.challengeContext,
  );

  const storageKey = `project_suggestion_${orgId}_${challengeId}_${challengeIdx}`;
  const getCachedSuggestion = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [storageKey]);
  const hasCachedData = !!getCachedSuggestion();

  const submittedStorageKey = storageKey + "_submitted";
  const getCachedSubmittedSuggestion = useCallback(() => {
    try {
      const raw = localStorage.getItem(submittedStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [submittedStorageKey]);
  const hasCachedSubmittedSuggestion = getCachedSubmittedSuggestion();

  const [suggestionId, setSuggestionId] = useState<number | null>(
    () => getCachedSuggestion()?.id ?? null,
  );

  const { data, isLoading } = ProjectSuggestions.useGetProjectSuggestion(suggestionId ?? 0, {
    query: { enabled: !!suggestionId },
  });
  const suggestion = data?.projectSuggestion;

  const hasCalledGenerate = useRef(false);
  const { mutate: generateSuggestions } =
    ProjectSuggestions.useGenerateBusinessChallengeSuggestion({
      mutation: {
        onSuccess: (data) => {
          const { ids } = data as { success: boolean; ids: number[] };
          setSuggestionId(ids[0]);
          setIsGenerating(false);
        },
      },
    });

  useEffect(() => {
    if (orgId && !hasCalledGenerate.current && !hasCachedData && !suggestionId) {
      hasCalledGenerate.current = true;
      generateSuggestions({
        data: {
          organizationId: orgId,
          challengeId: challengeIdValue,
          challengeIdx: challengeIdxValue,
        }
      });
    }
  }, [challengeIdxValue, challengeIdValue, generateSuggestions, hasCachedData, orgId, suggestionId]);

  const { mutate: createProject, isPending } = Projects.useCreateProject();

  const { data: orgData } = DirectoryOrganizations.useGetOrganizationById(orgId);

  const [isEditing, setIsEditing] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [submittedProjectId, setSubmittedProjectId] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(!hasCachedData);
  const [isAnimationLoading, setIsAnimationLoading] = useState<boolean>(!hasCachedData);
  const [isConfirmLeaveOpen, setIsConfirmLeaveOpen] = useState<boolean>(false);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      title: '',
      description: '',
      deliverables: [],
      learningOutcomes: '',
      skills: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'deliverables' });

  const formValues = watch();

  useEffect(() => {
    if (suggestion) {
      const cached = getCachedSuggestion();
      reset({
        title: cached?.title ?? suggestion.title,
        description: cached?.background ?? suggestion.background,
        deliverables: (cached?.deliverables ?? suggestion.deliverables).map((d: string) => ({ value: d })),
        learningOutcomes: cached?.learningOutcomes ?? suggestion.learningOutcomes,
        skills: cached?.skills ?? suggestion.skills,
      });
    }
  }, [suggestion, reset, getCachedSuggestion]);

  useEffect(() => {
    if (suggestion && !isGenerating && !getCachedSuggestion()) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(suggestion));
      } catch { /* ignore */ }
    }
  }, [suggestion, storageKey, isGenerating, getCachedSuggestion]);

  const challengeName = existingChallenges && existingChallenges.length >= challengeIdxValue
    ? businessChallengeFullText(existingChallenges[challengeIdxValue-1].challenge as string, true)
    : '';

  const handleEdit = () => setIsEditing(true);
  const handleConfirmLeave = () => navigate(`/platform${ROUTES.DASHBOARD}`);

  const onSave = handleSubmit((data) => {
    if (!suggestion) return;
    const updated = {
      ...suggestion,
      ...getCachedSuggestion(),
      title: data.title,
      background: data.description,
      deliverables: data.deliverables.map((d) => d.value),
      learningOutcomes: data.learningOutcomes,
      skills: data.skills,
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify(updated));
    } catch { /* ignore */ }
    setIsEditing(false);
  });

  const handleSubmitProject = () => {
    if (!suggestion) return;
    createProject(
      {
        data: {
          name: formValues.title || suggestion.title,
          roleTypes: [ProjectRoleType.consultant],
          businessChallenge: suggestion.businessChallenge,
          skills: formValues.skills,
          cohorts: null,
          description: formValues.description || suggestion.background,
          deliverables: formValues.deliverables.map((d) => d.value),
          learningOutcomes: formValues.learningOutcomes || suggestion.learningOutcomes,
          suggestionId: suggestionId,
        },
      },
      {
        onSuccess: (data) => {
          try {
            localStorage.removeItem(storageKey);
            localStorage.setItem(submittedStorageKey, JSON.stringify(true));
          } catch { /* ignore */ }
          const { created_project_ids: createdProjectIds } = data as {
            created_project_ids: number[];
          };
          setSubmittedProjectId(createdProjectIds[0]);
        },
      },
    );
  };

  if (submittedProjectId !== null || hasCachedSubmittedSuggestion) {
    return <ProjectSubmittedScreen />;
  }

  if (isPreviewOpen && orgData) {
    return (
      <ProjectSuggestionPreview
        title={formValues.title}
        description={formValues.description}
        deliverables={formValues.deliverables.map((d) => d.value)}
        skills={formValues.skills}
        learningOutcomes={formValues.learningOutcomes || suggestion?.learningOutcomes || ''}
        orgData={orgData}
        onClose={() => setIsPreviewOpen(false)}
      />
    )
  }

  return (
    <div className='w-full px-4 sm:px-10 text-gray-700'>
      <Button onClick={() => setIsConfirmLeaveOpen(true)} variant='link-color' size='sm' leadIcon='arrow-left'>
        Back to Home
      </Button>

      {isLoadingBusinessChallenges || isLoading || isGenerating || isAnimationLoading ? (
        <GeneratingSuggestionScreen
          challengeNumber={parseInt(challengeIdx)}
          organizationId={orgId}
          setLoading={(v) => setIsAnimationLoading(v)}
        />
      ) : (
        <div className='mt-8 max-w-[900px]'>
          <h1 className='text-3xl font-bold mb-1'>
            Your <span className='text-brand-500'>{challengeName}</span> project
          </h1>
          <p className='text-gray-500 mb-6'>
            Review and edit if needed before submitting. MovingWorlds will review the project before
            publishing.
          </p>

          {!isLoading && suggestion && (
            <div className='border border-gray-200 rounded-2xl overflow-hidden shadow-xs'>
              <div className='p-6 flex flex-col gap-6 bg-white'>
                <div>
                  <div className='flex justify-between items-center mb-2'>
                    <label className='font-bold text-sm text-gray-700'>Title</label>
                    <div className='flex gap-2'>
                      {isEditing ? (
                        <Button variant='primary' size='sm' leadIcon='check' onPress={() => { void onSave(); }}>
                          Save
                        </Button>
                      ) : (
                        <Button variant='secondary-gray' size='sm' leadIcon='edit-01' onPress={handleEdit}>
                          Edit
                        </Button>
                      )}
                      <Button
                        variant='secondary-gray'
                        size='sm'
                        leadIcon='eye'
                        isDisabled={!orgData}
                        onPress={() => setIsPreviewOpen(true)}
                      >
                        Preview
                      </Button>
                    </div>
                  </div>
                  {isEditing ? (
                    <Controller
                      control={control}
                      name='title'
                      rules={{ validate: (value) => value.trim().length > 0 || 'Title is required' }}
                      render={({ field }) => (
                        <Input
                          type='inline'
                          value={field.value}
                          onChange={field.onChange}
                          maxLength={80}
                          error={errors.title?.message}
                        />
                      )}
                    />
                  ) : (
                    <p className='font-bold text-lg text-gray-700'>
                      {formValues.title || suggestion.title}
                    </p>
                  )}
                </div>

                <div>
                  <p className='font-bold text-sm text-gray-700 mb-2'>Description</p>
                  {isEditing ? (
                    <Controller
                      control={control}
                      name='description'
                      rules={{ validate: (value) => value.trim().length > 0 || 'Description is required' }}
                      render={({ field }) => (
                        <Input
                          type='multiline'
                          value={field.value}
                          onChange={field.onChange}
                          characterCount={1000}
                          error={errors.description?.message}
                          autoGrow
                        />
                      )}
                    />
                  ) : (
                    <p>{formValues.description || suggestion.background}</p>
                  )}
                </div>

                <div>
                  <p className='font-bold text-sm text-gray-700 mb-2'>Possible deliverables</p>
                  {isEditing ? (
                    <div className='flex flex-col gap-4'>
                      {fields.map((field, i) => (
                        <div key={field.id} className='flex items-start gap-3'>
                          <div className='flex w-full'>
                            <div className='w-full'>
                            <Controller
                              control={control}
                              name={`deliverables.${i}.value`}
                              rules={{ validate: (value) => value.trim().length > 0 || 'Deliverable is required' }}
                              render={({ field: inputField }) => (
                                <Input
                                  type='multiline'
                                  value={inputField.value}
                                  onChange={inputField.onChange}
                                  characterCount={160}
                                  // maxLength={160}
                                  error={errors.deliverables?.[i]?.value?.message}
                                  autoGrow
                                  maxRows={3}
                                />
                              )}
                            />
                            </div>
                            <div className='w-15'>
                              <Button
                                onClick={() => remove(i)}
                                isDisabled={fields.length <= 1}
                                variant='tertiary-gray'
                                size='sm'
                                leadIcon='trash-01'
                                leadIconClassNames={fields.length > 1 ? 'stroke-error-500' : ''}
                              >
                                <></>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {fields.length < 10 && (
                        <Button
                          onClick={() => append({ value: '' })}
                          variant='link-color'
                          size='sm'
                          additionalClassnames='self-start'
                          leadIcon='plus-circle'
                        >
                          Add deliverable
                        </Button>
                      )}
                    </div>
                  ) : (
                    <ul className='flex flex-col gap-1'>
                      {formValues.deliverables.map((d, i) => (
                        <li key={i} className='flex gap-1'>
                          <span>•</span>
                          <span>{d.value}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <p className='font-bold text-sm text-gray-700 mb-2'>What this experience offers</p>
                  {isEditing ? (
                    <Controller
                      control={control}
                      name='learningOutcomes'
                      rules={{ validate: (value) => value.trim().length > 0 || 'This field is required' }}
                      render={({ field }) => (
                        <Input
                          type='multiline'
                          value={field.value}
                          onChange={field.onChange}
                          characterCount={1000}
                          error={errors.learningOutcomes?.message}
                          autoGrow
                        />
                      )}
                    />
                  ) : (
                    <p>{formValues.learningOutcomes || suggestion.learningOutcomes}</p>
                  )}
                </div>

                <div>
                  {isEditing ? (
                    <Controller
                      control={control}
                      name='skills'
                      rules={{ validate: (value) => value.length > 0 || 'Select at least 1 skill' }}
                      render={({ field }) => (
                        <SkillsSelectionDropdown
                          label={<p className='font-bold text-gray-700'>Preferred skills</p>}
                          subLabel={<p className='text-gray-500'>Select up to 5 skills</p>}
                          selectedSkills={field.value}
                          onSkillsSelectionChange={field.onChange}
                          selectionLimit={5}
                          placeholder='Select up to 5 skills'
                          placement='bottom start'
                          errors={errors.skills?.message ? { skills: { message: errors.skills.message } } : undefined}
                          mobile
                        />
                      )}
                    />
                  ) : (
                    <>
                      <p className='font-bold text-sm text-gray-700 mb-2'>Preferred skills</p>
                      <div className='flex gap-2 flex-wrap'>
                        {formValues.skills.map((skill, i) => (
                          <PillBadge key={i} variant='plain' color='blue' size='md'>
                            {skill}
                          </PillBadge>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className='flex flex-col-reverse items-center mt-6 gap-4 lg:grid lg:grid-cols-[1fr_auto_1fr]'>
            {!isEditing && (
              <p className='text-sm text-gray-500 lg:col-start-2 lg:text-center'>
                Not what you need?{' '}
                <a
                  href={`/platform${ROUTES.CREATE_PROJECT}/1`}
                  className='text-brand-500 hover:text-brand-600 font-medium'
                >
                  Create a project manually
                </a>
              </p>
            )}
            {!isEditing && (
              <div className='flex items-center gap-4 w-full flex-col-reverse lg:flex-row lg:col-start-3 lg:justify-self-end'>
                <Button
                  variant='secondary-gray'
                  size='lg'
                  onPress={() => setIsConfirmLeaveOpen(true)}
                  additionalClassnames={'max-lg:w-full'}
                >
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  size='lg'
                  isDisabled={!suggestion || isPending || formValues.skills.length < 1}
                  onPress={handleSubmitProject}
                  additionalClassnames={'max-lg:w-full w-[170px]'}
                >
                  Submit for review
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      <Modal
        isOpen={isConfirmLeaveOpen}
        variant='dialog'
        headerDivision='divider'
        title='Leave this page?'
        onClose={() => setIsConfirmLeaveOpen(false)}
        secondaryCTA={{ text: 'Leave this page', callback: handleConfirmLeave }}
        primaryCTA={{ text: 'Keep reviewing', buttonVariant: 'primary', callback: () => setIsConfirmLeaveOpen(false) }}
        ignoreExtraPadding
      >
        <p className='text-gray-500 mb-3'>If you leave now, this project may not be saved.</p>
        <p className='text-gray-500'>Don't worry about making it perfect, you can still make edits after it's been submitted for review.</p>
      </Modal>
    </div>
  );
};

export default GeneratedProjectSuggestionPage;